/**
 * Transport-aware wrapper for the `AddressesApi`: the paginated `list`, the
 * single-address DETAIL read (`getDetail`), plus the IDEMPOTENT overlay +
 * membership operations (assignment attach/detach + list, primary set/clear,
 * entity-mapping get/put/delete).
 *
 * Wraps the REST `AddressesApi` so each of those ops takes the fast GraphQL path
 * when the central transport registry says so, and TRANSPARENTLY falls back to
 * REST on any GraphQL error — flipping a flag on can never harden into an outage.
 * Everything else on the api (create/update/remove, share, wallet enrichment) is
 * delegated untouched: NON-idempotent writes stay on REST.
 *
 * `getByAddress` — the cross-chain lens — is routed too, through its OWN query
 * (`addressCrossChain`) rather than through `list`. That distinction is why it
 * went unwired for so long: `cross_chain` has no member of the `addresses`
 * field's scope enum, and the neighbouring `list()` comment saying so was read
 * as "GraphQL cannot serve the lens". It is a fact about that one query, not
 * about the API.
 *
 * `getDetail` is a pure read that resolves through the SAME commons
 * `address_read_facade` the REST detail route uses (one brain, two mouths), so
 * the row + `saved` + `other_scopes` (the row-tier scope switcher) cannot drift.
 * The write ops are routed because they are all idempotent on the backend
 * (deterministic overlay key / single-primary pointer / fixed-window mapping
 * upsert), which is exactly what makes a GraphQL-first-with-REST-fallback safe:
 * a retried or duplicated write converges to the same row.
 */
import type {
  AddressAssignment,
  AddressEntityMapping,
  AddressesApi,
  AssignmentAttachInput,
  AssignmentDetachInput,
  EntityMappingInput,
  EntityMemberPage,
  EntityMembershipQuery,
  EntityMembershipsInput,
  EntityMembershipsResult,
  PrimaryAddressPointer,
} from "@authe/fe-commons/addresses";
import type { FeatureFlags } from "@authe/fe-commons/flags";
import type { AddressScope, GraphQLClient } from "@authe/fe-commons/graphql";
import type { BatchOverlays, OverlayRef } from "@authe/fe-commons/overlays";
import {
  GraphQLRequestError,
  graphqlAttachAssignment,
  graphqlClearPrimary,
  graphqlDeleteEntityMapping,
  graphqlDetachAssignment,
  graphqlFetchAddressCrossChain,
  graphqlFetchAddressDetail,
  graphqlGetEntityMapping,
  graphqlListAddressesWithOverlays,
  addressListUnroutable,
  entityMembershipsUnroutable,
  graphqlListAssignments,
  graphqlListEntityMemberships,
  graphqlPutEntityMapping,
  graphqlPutEntityMemberships,
  graphqlSetPrimary,
  toAddressScope,
} from "@authe/fe-commons/graphql";
import {
  resolveTransport,
  type DataOperation,
  type TransportCapabilitySource,
} from "@authe/fe-commons/transport";
import { createLogger } from "@authe/fe-commons/logging";

const log = createLogger("dashboard:graphql-addresses");

export interface TransportAwareAddressesOptions {
  /** The GraphQL client, or `null` when unconfigured (always REST). */
  graphqlClient: GraphQLClient | null;
  /** The live feature-flag set (registry precedence: master + per-op). */
  flags: FeatureFlags;
  /**
   * The caller's plan ceiling, from `<TransportCapabilitiesProvider>`.
   *
   * Passed in rather than read here because this is not a component. Omitting it
   * means unrestricted, which is the same thing the context defaults to — so a
   * host with no plan model behaves as it always did.
   */
  capabilities?: TransportCapabilitySource;
  /**
   * Hand the overlays that rode along with a GraphQL page to whoever caches them.
   *
   * The GraphQL list returns each row's resolved overlays with the page, which
   * the REST list cannot; without somewhere to put them a table still runs its
   * `POST /addresses/catalog/resolve` after every page and every "Load more", and
   * the whole point of the richer selection is lost. Optional, and deliberately a
   * callback rather than a query client: `list` must keep answering in the REST
   * DTO for the fallback to stay transparent, so this is the second door the
   * extra data leaves through and nothing downstream is required to open it.
   */
  seedOverlays?: (overlays: BatchOverlays, refs: OverlayRef[], scope?: AddressScope) => void;
  /**
   * The tenancy tier to resolve the page's overlays at — the caller's configured
   * default view scope. Must match what the overlay batch would have asked for,
   * or the seed answers a question nobody asked.
   */
  resolveScope?: AddressScope;
}

/**
 * Run `op` over GraphQL when the registry routes it there, else (or on any
 * GraphQL transport error) fall back to the REST call. A non-GraphQL error from
 * the GraphQL path is re-thrown (a programming error must not be masked). The
 * fallback runs the REST closure so a flipped flag can never cause an outage.
 */
/**
 * Everything the routing decision needs, as ONE value.
 *
 * Collapsed from three positional parameters because twelve call sites were
 * threading `graphqlClient, flags` by hand, and adding a fourth input (the plan
 * ceiling) to that shape means editing twelve argument lists and hoping. A
 * context object is one edit and cannot be passed in the wrong order.
 */
interface RouteContext {
  graphqlClient: GraphQLClient | null;
  flags: FeatureFlags;
  capabilities?: TransportCapabilitySource;
}

async function route<T>(
  operation: DataOperation,
  { graphqlClient, flags, capabilities }: RouteContext,
  runGraphql: (client: GraphQLClient) => Promise<T>,
  runRest: () => Promise<T>,
): Promise<T> {
  const wantsGraphql =
    graphqlClient != null &&
    // The real `graphqlConfigured`, not a hardcoded `true` — the registry already
    // owns "no endpoint means REST", and restating it as a constant here while
    // re-deriving it from `graphqlClient` on the line above is one rule in two
    // places, which is how the two come to disagree. Plus the plan ceiling, which
    // a hand-assembled resolve otherwise silently ignores.
    resolveTransport(operation, flags, {
      graphqlConfigured: graphqlClient != null,
      capabilities,
    }) === "graphql";
  if (wantsGraphql && graphqlClient) {
    try {
      return await runGraphql(graphqlClient);
    } catch (err) {
      if (!(err instanceof GraphQLRequestError)) throw err;
      log.warn(`${operation} GraphQL failed, falling back to REST`, {
        status: err.status,
        message: err.message,
      });
    }
  }
  return runRest();
}

export function withGraphqlAddresses(
  rest: AddressesApi,
  { graphqlClient, flags, capabilities, seedOverlays, resolveScope }: TransportAwareAddressesOptions,
): AddressesApi {
  const ctx: RouteContext = { graphqlClient, flags, capabilities };
  return {
    ...rest,

    async list(query, signal) {
      // Straight to REST for anything the `addresses` field cannot express —
      // NOT through `route`, and for two different reasons depending on which
      // argument it is. A `cross_chain` scope would at least fail loudly (a
      // schema error the fallback catches, after paying for the request on every
      // page). The single-address SEARCH would not fail at all: `addresses` has
      // no such argument, so the request succeeds and answers page one of the
      // whole tenant list — a wrong answer dressed as a right one, with no error
      // for the fallback to catch. `addressListUnroutable` owns both, beside the
      // document that drops them.
      if (addressListUnroutable(query) !== null) return rest.list(query, signal);

      return route(
        "address.list",
        ctx,
        async (client) => {
          const { page, overlays } = await graphqlListAddressesWithOverlays(
            client,
            query,
            { signal },
            resolveScope,
          );
          // The refs the batch hook would build for this page, so the seed lands
          // on the key it will look under. Derived from the page rather than
          // passed in, because only the response knows which rows came back.
          seedOverlays?.(
            overlays,
            page.addresses.map((a) => ({ address: a.address, chainId: a.chain })),
            resolveScope,
          );
          return page;
        },
        () => rest.list(query, signal),
      );
    },

    async getByAddress(address, signal) {
      return route(
        "address.crossChain",
        ctx,
        (client) =>
          graphqlFetchAddressCrossChain(client, address, { resolveScope }, { signal }),
        () => rest.getByAddress(address, signal),
      );
    },

    async getDetail(chain, address, opts, signal) {
      return route(
        "address.detail",
        ctx,
        async (client) => {
          const detail = await graphqlFetchAddressDetail(
            client,
            {
              address,
              chainId: chain,
              withScopes: opts?.withScopes ?? false,
              // The widen is derived from the picked scope's tier name (`tenant`
              // / `org` / …) → the SDL `AddressScope` enum; a non-widenable tier
              // (e.g. `team`) maps to `undefined`, i.e. the caller's own view.
              scope: opts?.scope ? toAddressScope(opts.scope.scope) : undefined,
            },
            { signal },
          );
          // The resolver never 404s (it synthesizes an unsaved row), so `null`
          // is effectively unreachable; fall back to REST if it ever happens.
          return detail ?? rest.getDetail(chain, address, opts, signal);
        },
        () => rest.getDetail(chain, address, opts, signal),
      );
    },

    async listAssignments(chain: string, address: string, signal?: AbortSignal): Promise<AddressAssignment[]> {
      return route(
        "address.assignments.list",
        ctx,
        (client) => graphqlListAssignments(client, { address, chainId: chain }, { signal }),
        () => rest.listAssignments(chain, address, signal),
      );
    },

    async attachAssignment(
      chain: string,
      address: string,
      input: AssignmentAttachInput,
    ): Promise<AddressAssignment> {
      return route(
        "address.assignments.write",
        ctx,
        (client) => graphqlAttachAssignment(client, { address, chainId: chain, input }),
        () => rest.attachAssignment(chain, address, input),
      );
    },

    async detachAssignment(chain: string, address: string, input: AssignmentDetachInput): Promise<void> {
      return route(
        "address.assignments.write",
        ctx,
        (client) => graphqlDetachAssignment(client, { address, chainId: chain, input }),
        () => rest.detachAssignment(chain, address, input),
      );
    },

    async setPrimary(chain: string, address: string, entityId: string): Promise<PrimaryAddressPointer> {
      return route(
        "address.primary.write",
        ctx,
        (client) => graphqlSetPrimary(client, { address, chainId: chain, entityId }),
        () => rest.setPrimary(chain, address, entityId),
      );
    },

    async clearPrimary(chain: string, address: string, entityId: string): Promise<void> {
      return route(
        "address.primary.write",
        ctx,
        (client) => graphqlClearPrimary(client, { chainId: chain, entityId }),
        () => rest.clearPrimary(chain, address, entityId),
      );
    },

    async getEntityMapping(
      chain: string,
      address: string,
      signal?: AbortSignal,
    ): Promise<AddressEntityMapping | null> {
      return route(
        "address.entityMapping.read",
        ctx,
        (client) => graphqlGetEntityMapping(client, { address, chainId: chain }, { signal }),
        () => rest.getEntityMapping(chain, address, signal),
      );
    },

    async putEntityMapping(
      chain: string,
      address: string,
      input: EntityMappingInput,
    ): Promise<AddressEntityMapping> {
      return route(
        "address.entityMapping.write",
        ctx,
        (client) => graphqlPutEntityMapping(client, { address, chainId: chain, input }),
        () => rest.putEntityMapping(chain, address, input),
      );
    },

    async deleteEntityMapping(chain: string, address: string): Promise<void> {
      await route(
        "address.entityMapping.write",
        ctx,
        (client) => graphqlDeleteEntityMapping(client, { address, chainId: chain }),
        async () => {
          await rest.deleteEntityMapping(chain, address);
          return 0;
        },
      );
    },

    async listEntityMemberships(query: EntityMembershipQuery, signal?: AbortSignal): Promise<EntityMemberPage> {
      // `chains` widens the headline pointer's coverage and has no GraphQL
      // argument, so a routed read would answer with page-derived coverage — a
      // missing star that reads as "no headline set on this chain".
      if (entityMembershipsUnroutable(query) !== null) {
        return rest.listEntityMemberships(query, signal);
      }
      return route(
        "address.memberships.read",
        ctx,
        (client) => graphqlListEntityMemberships(client, query, { signal }),
        () => rest.listEntityMemberships(query, signal),
      );
    },

    async putEntityMemberships(input: EntityMembershipsInput): Promise<EntityMembershipsResult> {
      // Idempotent PER MEMBER (each upserts on its own deterministic key), so a
      // GraphQL failure can be retried over REST without double-mapping whichever
      // members the first attempt already wrote.
      return route(
        "address.memberships.write",
        ctx,
        (client) => graphqlPutEntityMemberships(client, input),
        () => rest.putEntityMemberships(input),
      );
    },
  };
}
